import { useQuery } from '@tanstack/react-query'
import { fetch } from 'src/service/api/fetcher'
import { useQueryOptionInterface, useQueryReturnData } from 'src/types/react-query'
import { CMDTYPES } from 'src/service/constant/cmdtypes'
import {
  BrandListSpace,
  CategoryListSpace,
  CouponDetailInfoTabSpace,
  FreeCouponListSpace,
  FreeCouponDetailSpace,
  HotDealItemListsSpace,
  PopularCouponListSpace,
  ServiceTermsSpace,
  TermsListSpace,
  PurchaseHistoryDetailSpace,
  BankListSpace,
  NotifyListSpace,
  SelfRegisterCouponDetailSpace,
  AlwaysCouponDetailSpace,
  HotDealCouponDetailSpace,
  CouponDetailRequestType,
  CouponDetailResponseType,
  WalletCouponUsedCheckSpace,
  WalletImminentCouponSpace,
  CouponPurchasedInfo,
  CouponCancelValidSpace,
  WalletCouponSpace,
  PopupBrandListSpace,
  PurchaseHistoryListSpace,
  ResponseDataType,
  BannerSpace,
  MdPickCouponSpace,
  RecommendBrandListSpace,
  MostViewedCouponListSpace,
  PassCertCheckSpace,
  GiftMsgCardListSpace,
  GiftShareUrlInfoSpace,
  GiftDetailInfoByOrderIdSpace,
  GiftedCouponDetailSpace,
  GiftDetailInfoByGiftIdxSpace,
  GiftTransMsgResultSpace,
  GiftHotDealListsSpace,
  HotDealPurchaseLimitInfoSpace,
  DownloadCouponPathCheckSpace,
} from 'src/types/api'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import { captureMessage, SENTRY_SEVERITY } from 'src/utils/sentry'

//T : request body type, U: response body type
export const useCustomQuery = <U>(option: useQueryOptionInterface<U>): useQueryReturnData<U> => {
  const { data, isSuccess, isLoading, refetch, isRefetching } = useQuery(option)

  return {
    data: data as ResponseDataType<U>,
    isLoading,
    isSuccess,
    refetch,
    isRefetching,
  }
}

// 세션체크(세션 유지용 10분에 한번씩 리패치)
export const sessionCheckQueryOption = (when: boolean): useQueryOptionInterface<unknown> => ({
  queryKey: ['session_check'],
  queryFn: async () => await fetch.post<unknown, unknown>(CMDTYPES.SessionCheck),
  refetchIntervalInBackground: true,
  refetchInterval: 60 * 1000 * 10,
  refetchOnReconnect: true,
  refetchOnWindowFocus: true,
  enabled: when,
  networkMode: 'offlineFirst',
})

// 가입페이지- 약관리스트
export const termsListQueryOption = (
  data: TermsListSpace.Request,
): useQueryOptionInterface<TermsListSpace.Response> => ({
  queryKey: ['terms_list', `${data?.Telecom}`],
  queryFn: async () =>
    await fetch.post<TermsListSpace.Request, TermsListSpace.Response>(CMDTYPES.TermsList, data),
  networkMode: 'offlineFirst',
})

// 메인 페이지 - 카테고리 리스트
export const categoryListQueryOption = (): useQueryOptionInterface<CategoryListSpace.Response> => ({
  queryKey: ['category_list'],
  queryFn: async () =>
    await fetch.post<CategoryListSpace.Request, CategoryListSpace.Response>(CMDTYPES.CategoryList),
  networkMode: 'offlineFirst',
  staleTime: 30 * 1000,
})

// 브랜드 페이지 - 브랜드 리스트
export const brandListQueryOption = (): useQueryOptionInterface<BrandListSpace.Response> => ({
  queryKey: ['brand_list'],
  queryFn: async () =>
    await fetch.post<BrandListSpace.Request, BrandListSpace.Response>(CMDTYPES.BrandList),
  staleTime: 30 * 1000,
  networkMode: 'offlineFirst',
})
//검색화면 인기 쿠폰 리스트
export const searchPopularCouponQueryOption =
  (): useQueryOptionInterface<PopularCouponListSpace.Response> => ({
    queryKey: ['search_popular_coupon'],
    queryFn: async () => {
      const res = await fetch.post<PopularCouponListSpace.Request, PopularCouponListSpace.Response>(
        CMDTYPES.SearchPopularCoupon,
      )
      if (res.Header.ErrCode === ERRCODE_SUCCESS && !res.Body?.PopularCoupon?.length) {
        captureMessage(`[PopularCoupon] Empty list (ErrCode = ${res.Header.ErrCode})`, {
          level: SENTRY_SEVERITY.Info,
          extra: { response: res },
        })
      }
      return res
    },
    networkMode: 'offlineFirst',
  })

export const couponDetailQueryOption = (
  couponType: string,
  data: CouponDetailRequestType,
  when?: boolean,
): useQueryOptionInterface<CouponDetailResponseType> => {
  const hotDeal = couponType === '1'
  return hotDeal
    ? hotdealCouponDetailQueryOption(data, when)
    : alwaysCouponDetailQueryOption(data, when)
}

const alwaysCouponDetailQueryOption = (
  data: AlwaysCouponDetailSpace.Request,
  when?: boolean,
): useQueryOptionInterface<AlwaysCouponDetailSpace.Response> => ({
  queryKey: ['always_coupon_detail', `${data.CouponIdx}`],
  queryFn: async () =>
    await fetch.post<AlwaysCouponDetailSpace.Request, AlwaysCouponDetailSpace.Response>(
      CMDTYPES.AlwaysCouponDetailInfo,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: when && true,
})

const hotdealCouponDetailQueryOption = (
  data: HotDealCouponDetailSpace.Request,
  when?: boolean,
): useQueryOptionInterface<HotDealCouponDetailSpace.Response> => ({
  queryKey: ['hotdeal_coupon_detail', `${data.CouponIdx}`],
  queryFn: async () =>
    await fetch.post<HotDealCouponDetailSpace.Request, HotDealCouponDetailSpace.Response>(
      CMDTYPES.HotDealDetailInfo,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: when && true,
})

// 쿠폰 상세보기/ 상세정보탭 정보
export const couponDetailInfoTabQueryOption = (
  data: CouponDetailInfoTabSpace.Request,
): useQueryOptionInterface<CouponDetailInfoTabSpace.Response> => ({
  queryKey: ['coupon_detail_info_tab', `${data?.CouponType}`, `${data?.CouponNumber}`],
  queryFn: async () =>
    await fetch.post<CouponDetailInfoTabSpace.Request, CouponDetailInfoTabSpace.Response>(
      CMDTYPES.CouponDetailInfoTab,
      data,
    ),
  networkMode: 'offlineFirst',
})
//메인페이지- 핫딜리스트
export const hotDealCouponListQueryOption =
  (): useQueryOptionInterface<HotDealItemListsSpace.Response> => ({
    queryKey: ['hot_deal_list'],
    queryFn: async () =>
      await fetch.post<HotDealItemListsSpace.Request, HotDealItemListsSpace.Response>(
        CMDTYPES.HotDealCouponList,
      ),
    staleTime: 1000,
    refetchOnWindowFocus: true,
    networkMode: 'offlineFirst',
  })

//약관 한개 조회
export const serviceTermsQueryOption = (
  data: ServiceTermsSpace.Request,
): useQueryOptionInterface<ServiceTermsSpace.Response> => ({
  queryKey: ['service_terms', `${data?.Type}`],
  queryFn: () =>
    fetch.post<ServiceTermsSpace.Request, ServiceTermsSpace.Response>(CMDTYPES.ServiceTerms, data),
  networkMode: 'offlineFirst',
})
//무료쿠폰 목록
export const FreeCouponListQueryOption = (
  when?: boolean,
): useQueryOptionInterface<FreeCouponListSpace.Response> => ({
  queryKey: ['free_coupon_list'],
  queryFn: () =>
    fetch.post<FreeCouponListSpace.Request, FreeCouponListSpace.Response>(CMDTYPES.FreeCouponList),
  staleTime: 2000,
  networkMode: 'offlineFirst',
  enabled: (when ?? true) && true,
})
//무료쿠폰 상세정보
export const freeCouponDetailQueryOption = (
  data: FreeCouponDetailSpace.Request,
): useQueryOptionInterface<FreeCouponDetailSpace.Response> => ({
  queryKey: ['free_coupon_detail'],
  queryFn: () =>
    fetch.post<FreeCouponDetailSpace.Request, FreeCouponDetailSpace.Response>(
      CMDTYPES.FreeCouponDetail,
      data,
    ),
  networkMode: 'offlineFirst',
})
//쿠폰 사용 여부 조회 (쿠폰 지갑)
export const walletCouponUsedCheckQueryOption = (
  data: WalletCouponUsedCheckSpace.Request,
  when: boolean,
): useQueryOptionInterface<WalletCouponUsedCheckSpace.Response> => ({
  queryKey: ['wallet_coupon_used_check', `${data?.CouponOrderID}`],
  queryFn: () =>
    fetch.post<WalletCouponUsedCheckSpace.Request, WalletCouponUsedCheckSpace.Response>(
      CMDTYPES.walletCouponUsedCheck,
      data,
    ),
  staleTime: 2000,
  enabled: when,
  networkMode: 'offlineFirst',
})

export const purchaseHistoryDetailQueryOption = (
  data: PurchaseHistoryDetailSpace.Request,
): useQueryOptionInterface<PurchaseHistoryDetailSpace.Response> => ({
  queryKey: ['purchase_history_list_detail', `${data?.Idx}`],
  queryFn: () =>
    fetch.post<PurchaseHistoryDetailSpace.Request, PurchaseHistoryDetailSpace.Response>(
      CMDTYPES.PurchaseCouponDetail,
      data,
    ),
  networkMode: 'offlineFirst',
})
export const refundBankListQueryOption = (): useQueryOptionInterface<BankListSpace.Response> => ({
  queryKey: ['refund_bank_list'],
  queryFn: () => fetch.post<BankListSpace.Request, BankListSpace.Response>(CMDTYPES.RefundBankList),
  networkMode: 'offlineFirst',
})

export const notifyListQueryOption = (): useQueryOptionInterface<NotifyListSpace.Response> => ({
  queryKey: ['notify_list'],
  queryFn: () =>
    fetch.post<NotifyListSpace.Request, NotifyListSpace.Response>(
      CMDTYPES.NotificationReceivedList,
    ),
  networkMode: 'offlineFirst',
})

export const walletImminentCouponQueryOption =
  (): useQueryOptionInterface<WalletImminentCouponSpace.Response> => ({
    queryKey: ['wallet_imminent_coupon'],
    queryFn: () =>
      fetch.post<WalletImminentCouponSpace.Request, WalletImminentCouponSpace.Response>(
        CMDTYPES.walletImminentCoupon,
      ),
    staleTime: 1000 * 5,
    networkMode: 'offlineFirst',
  })

export const selfRegisterDetailQueryOption = (
  data: SelfRegisterCouponDetailSpace.Request,
): useQueryOptionInterface<SelfRegisterCouponDetailSpace.Response> => ({
  queryKey: ['self_register_detail', `${data.Idx}`],
  queryFn: () =>
    fetch.post<SelfRegisterCouponDetailSpace.Request, SelfRegisterCouponDetailSpace.Response>(
      CMDTYPES.SelfRegisterCouponDetail,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

export const couponPurchasedInfoQueryOption = (
  data: CouponPurchasedInfo.Request,
  when?: boolean,
): useQueryOptionInterface<CouponPurchasedInfo.Response> => ({
  queryKey: ['coupon_purchased_info', `${data.Idx}`],
  queryFn: () =>
    fetch.post<CouponPurchasedInfo.Request, CouponPurchasedInfo.Response>(
      CMDTYPES.CouponPurchasedInfo,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: when && true,
})

// 쿠폰 구매 취소 가능 검증
export const cancelCouponValidQueryOption = (
  data: CouponCancelValidSpace.Request,
): useQueryOptionInterface<CouponCancelValidSpace.Response> => ({
  queryKey: ['cancel_coupon_valid', `${data.Idx}`],
  queryFn: () =>
    fetch.post<CouponCancelValidSpace.Request, CouponCancelValidSpace.Response>(
      CMDTYPES.CancelCouponValidCheck,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

// 보유 전체 쿠폰 리스트
export const ownedCouponFullListQueryOption =
  (): useQueryOptionInterface<WalletCouponSpace.Response> => ({
    queryKey: ['wallet_coupon_full_list'],
    queryFn: () =>
      fetch.post<WalletCouponSpace.Request, WalletCouponSpace.Response>(CMDTYPES.walletCouponList, {
        Order: 0,
        Length: '10000',
        Offset: '0',
      }),
    staleTime: 1000 * 5,
    networkMode: 'offlineFirst',
  })

export const popularBrandQueryOption =
  (): useQueryOptionInterface<PopupBrandListSpace.Response> => ({
    queryKey: ['popular_brand_list'],
    queryFn: () =>
      fetch.post<PopupBrandListSpace.Request, PopupBrandListSpace.Response>(
        CMDTYPES.PopularBrandList,
        {},
      ),
    staleTime: 1000,
    networkMode: 'offlineFirst',
  })

// 특정 브랜드 보유 쿠폰
export const possessionBrandCouponQueryOption = (
  brandNumber: number | undefined,
  tel: string,
): useQueryOptionInterface<PurchaseHistoryListSpace.Response> => ({
  queryKey: ['purchase_coupon_list', `${brandNumber}`, tel],
  queryFn: () =>
    fetch.post<PurchaseHistoryListSpace.Request, PurchaseHistoryListSpace.Response>(
      CMDTYPES.PurchaseCouponList,
      {
        Offset: '0',
        Length: '10000',
        Type: 0,
        BrandNumber: brandNumber as number,
      },
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: !!brandNumber,
})

// //카카오맵 위치정보 사용동의
// export const kakaomapInfoAuthStatusQueryOption =
//   (): useQueryOptionInterface<KakaoMapInfoAuthStatusSpace.Response> => ({
//     queryKey: ['kakao_map_info_auth_status'],
//     queryFn: () =>
//       fetch.post<KakaoMapInfoAuthStatusSpace.Request, KakaoMapInfoAuthStatusSpace.Response>(
//         CMDTYPES.KAKAOMAPINFOAUTH,
//         {},
//       ),
//     staleTime: 1000,
//     networkMode: 'offlineFirst',
//   })

//메인 화면 최상단 배너 컨텐츠
export const mainTopBannerListQueryOption = (): useQueryOptionInterface<BannerSpace.Response> => ({
  queryKey: ['main_top_banner_list'],
  queryFn: () =>
    fetch.post<BannerSpace.Request, BannerSpace.Response>(CMDTYPES.BannerList, { Type: 0 }),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

//메인화면 MD 추천 쿠폰 리스트
export const mdPickCouponListQueryOption =
  (): useQueryOptionInterface<MdPickCouponSpace.Response> => ({
    queryKey: ['md_pick_coupon_list'],
    queryFn: () =>
      fetch.post<MdPickCouponSpace.Request, MdPickCouponSpace.Response>(
        CMDTYPES.MdPickCouponList,
        {},
      ),
    staleTime: 1000,
    networkMode: 'offlineFirst',
  })

//메인화면 추천 브랜드 리스트
export const recommendBrandListQueryOption =
  (): useQueryOptionInterface<RecommendBrandListSpace.Response> => ({
    queryKey: ['recommend_brand_list'],
    queryFn: () =>
      fetch.post<RecommendBrandListSpace.Request, RecommendBrandListSpace.Response>(
        CMDTYPES.RecommendBrandList,
        {},
      ),
    staleTime: 1000,
    networkMode: 'offlineFirst',
  })

//사람들이 많이 본 상품
export const mostViewedCouponListQueryOption =
  (): useQueryOptionInterface<MostViewedCouponListSpace.Response> => ({
    queryKey: ['most_viewed_coupon_list'],
    queryFn: () =>
      fetch.post<MostViewedCouponListSpace.Request, MostViewedCouponListSpace.Response>(
        CMDTYPES.MostViewedCouponList,
        {},
      ),
    staleTime: 1000,
    networkMode: 'offlineFirst',
  })

//PASS 본인인증 여부
export const passCertCheckQueryOption = (
  when?: boolean,
): useQueryOptionInterface<PassCertCheckSpace.Response> => ({
  queryKey: ['pass_cert_check'],
  queryFn: () =>
    fetch.post<PassCertCheckSpace.Request, PassCertCheckSpace.Response>(CMDTYPES.PassCertCheck, {}),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: when && true,
})

export const hotDealPurchaseLimitInfoQueryOption =
  (): useQueryOptionInterface<HotDealPurchaseLimitInfoSpace.Response> => ({
    queryKey: ['hot_deal_purchase_limit_info'],
    queryFn: () =>
      fetch.post<HotDealPurchaseLimitInfoSpace.Request, HotDealPurchaseLimitInfoSpace.Response>(
        CMDTYPES.HotDealPurchaseLimitInfo,
      ),
    staleTime: 1000,
    networkMode: 'offlineFirst',
  })

//선물하기 - 선물카드 목록 조회
export const giftMsgCardListQueryOption = (
  data: GiftMsgCardListSpace.Request,
): useQueryOptionInterface<GiftMsgCardListSpace.Response> => ({
  queryKey: ['gift_msg_card_list', `${data.Type}`],
  queryFn: async () =>
    await fetch.post<GiftMsgCardListSpace.Request, GiftMsgCardListSpace.Response>(
      CMDTYPES.GiftMsgCardList,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

//선물하기 - 구매한 선물정보 조회
export const giftDetailInfoByOrderIdQueryOption = (
  data: GiftDetailInfoByOrderIdSpace.Request,
  when?: boolean,
): useQueryOptionInterface<GiftDetailInfoByOrderIdSpace.Response> => ({
  queryKey: ['gift_detail_info_by_order_id', `${data.OrderID}`],
  queryFn: async () =>
    await fetch.post<GiftDetailInfoByOrderIdSpace.Request, GiftDetailInfoByOrderIdSpace.Response>(
      CMDTYPES.GiftDetailInfoByOrderId,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: when && true,
})
export const giftDetailInfoByGiftIdxQueryOption = (
  data: GiftDetailInfoByGiftIdxSpace.Request,
): useQueryOptionInterface<GiftDetailInfoByGiftIdxSpace.Response> => ({
  queryKey: ['gift_detail_info_by_gift_idx', `${data.GiftIdx}`],
  queryFn: async () =>
    await fetch.post<GiftDetailInfoByGiftIdxSpace.Request, GiftDetailInfoByGiftIdxSpace.Response>(
      CMDTYPES.GiftDetailInfoByGiftIdx,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

//선물하기 - 카카오공유 및 url 정보 조회
export const giftShareUrlInfoQueryOption = (
  data: GiftShareUrlInfoSpace.Request,
  when?: boolean,
): useQueryOptionInterface<GiftShareUrlInfoSpace.Response> => ({
  queryKey: ['gift_share_url_info', `${data.GiftIdx}`],
  queryFn: async () =>
    await fetch.post<GiftShareUrlInfoSpace.Request, GiftShareUrlInfoSpace.Response>(
      CMDTYPES.GiftShareUrlInfo,
      data,
    ),
  staleTime: 1000,
  networkMode: 'offlineFirst',
  enabled: when && true,
})

//선물하기 - 선물한 쿠폰 상세
export const giftedCouponDetailQueryOption = (
  data: GiftedCouponDetailSpace.Request,
  when?: boolean,
): useQueryOptionInterface<GiftedCouponDetailSpace.Response> => ({
  queryKey: ['gifted_coupon_detail', `${data?.GiftIdx}`],
  queryFn: () =>
    fetch.post<GiftedCouponDetailSpace.Request, GiftedCouponDetailSpace.Response>(
      CMDTYPES.GiftedCouponDetail,
      data,
    ),
  networkMode: 'offlineFirst',
  enabled: when && true,
})

//선물하기 - 문자 발송 결과 조회
export const giftTransMsgResultQueryOption = (
  data: GiftTransMsgResultSpace.Request,
  when?: boolean,
): useQueryOptionInterface<GiftTransMsgResultSpace.Response> => ({
  queryKey: ['trans_msg_result', `${data?.TransMsgIdx}`],
  queryFn: () =>
    fetch.post<GiftTransMsgResultSpace.Request, GiftTransMsgResultSpace.Response>(
      CMDTYPES.GiftTransMsgResult,
      data,
    ),
  networkMode: 'offlineFirst',
  enabled: when && true,
  refetchInterval: 3000,
})

//선물하기 - 선물받은 쿠폰 배너
export const giftCardBannerListQueryOption = (
  data: BannerSpace.Request,
): useQueryOptionInterface<BannerSpace.Response> => ({
  queryKey: ['gift_card_banner_list', `${data?.Type}`],
  queryFn: () => fetch.post<BannerSpace.Request, BannerSpace.Response>(CMDTYPES.BannerList, data),
  staleTime: 1000,
  networkMode: 'offlineFirst',
})

//선물하기 - 핫딜
export const giftHotDealListQueryOption = (
  data: GiftHotDealListsSpace.Request,
): useQueryOptionInterface<GiftHotDealListsSpace.Response> => ({
  queryKey: ['gift_hot_deal_list', `${data?.Type}`],
  queryFn: async () =>
    await fetch.post<GiftHotDealListsSpace.Request, GiftHotDealListsSpace.Response>(
      CMDTYPES.GiftHotDealList,
      { Type: data?.Type, Offset: data?.Offset || '0', Length: data?.Length || '10000' },
    ),
  staleTime: 1000,
  refetchOnWindowFocus: true,
  networkMode: 'offlineFirst',
})

//쿠폰 이미지 다운로드 url 체크
export const downloadCouponPathCheckQueryOption = (
  data: DownloadCouponPathCheckSpace.Request,
): useQueryOptionInterface<DownloadCouponPathCheckSpace.Response> => ({
  queryKey: ['download_coupon_path_check', `${data?.Idx}`, `${data?.ImgPath}`],
  queryFn: () =>
    fetch.post<DownloadCouponPathCheckSpace.Request, DownloadCouponPathCheckSpace.Response>(
      CMDTYPES.DownloadCouponPathCheck,
      data,
    ),
  networkMode: 'offlineFirst',
})
